import {
  Card,
  FormField,
  Country,
  FormControl,
  IconButton,
  openDialog,
  closeCurrentDialog,
  Tooltip,
} from 'platform/components';
import {Grid, GridItem, Hide, HStack, VStack, Stack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {
  CustomerSelectSearch,
  useEditCustomer,
  CustomerMatchOrCreate,
  getCustomerName,
  getPhoneNumber,
  getSaneCustomerContactDetails,
  getSaneCustomerContactIds,
  SaneCustomer,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useDriverCustomerData} from '../hooks/useDriverCustomerData';
import {DriverInformationFormFields} from '../schemas/driverInformationSchema';

interface DriverInformationSectionProps extends TestIdProps {
  serviceCaseId: string;
  control: FormControl<DriverInformationFormFields>;
  formApi: UseFormReturn<DriverInformationFormFields>;
  countriesOptions: Country[];
  phoneInputKey: number;
  refreshPhoneInputKey: () => void;
  isReadOnly?: boolean;
}

export const DriverInformationSection = (props: DriverInformationSectionProps) => {
  const {formApi, countriesOptions, refreshPhoneInputKey} = props;

  const {customerData, fallbackCustomerData} = useDriverCustomerData({
    serviceCaseId: props.serviceCaseId,
    formApi,
  });

  const handleCustomerSelect = (data: SaneCustomer) => {
    const {name, email, phoneNumber, prefix} = getSaneCustomerContactDetails(data);

    const formattedPhoneNumber = getPhoneNumber(prefix, phoneNumber, countriesOptions);

    formApi.setValue('driverFullName', name);
    formApi.setValue('driverEmail', email);
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverIdentityCard', data.identityCards[0]?.cardNumber);
    formApi.setValue('driverId', data.customerId);
    formApi.setValue('driverContactId', getSaneCustomerContactIds(data).contactId);
    formApi.setValue('contactPerson', data.customerId);
  };

  const handleCustomerCreated = (customer: CustomerResponseBodyV2) => {
    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
    const phoneNumber = customerPhoneNumber;
    const name = getCustomerName(customer);

    const formattedPhoneNumber = getPhoneNumber(
      phoneNumber?.prefix ?? '',
      phoneNumber?.number ?? '',
      countriesOptions
    );

    formApi.setValue('driverFullName', name ?? '');
    formApi.setValue('driverEmail', email || '');
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverId', customer.id);
    formApi.setValue('driverContactId', customer.primaryContactId ?? null);
    formApi.setValue('contactPerson', customer.id);
    formApi.setValue('driverIdentityCard', customerInfo.identityCards?.[0]?.cardNumber ?? '');
    refreshPhoneInputKey();
    closeCurrentDialog();
  };

  const handleCreateCustomer = () => {
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={handleCustomerCreated}
        data-testid={suffixTestId('createCustomer', props)}
      />,
      {
        title: i18n.t('entity.customer.labels.customer'),
      }
    );
  };

  const editCustomer = useEditCustomer(customerData ?? fallbackCustomerData, (customer) => {
    const customerInfo = customer?.foundingPerson ?? customer?.contacts?.[0];

    if (!customerInfo) {
      return;
    }

    const email = customerInfo.emails?.[0]?.email;
    const customerPhoneNumber = customerInfo.phoneNumbers?.[0];
    const phoneNumber = customerPhoneNumber;
    const name = getCustomerName(customer);

    const formattedPhoneNumber = getPhoneNumber(
      phoneNumber?.prefix ?? '',
      phoneNumber?.number ?? '',
      countriesOptions
    );

    formApi.setValue('driverFullName', name ?? '');
    formApi.setValue('driverEmail', email || '');
    formApi.setValue('driverPhone', {
      countryCode: formattedPhoneNumber.countryCode || 'CZE',
      number: formattedPhoneNumber.number || '',
      prefix: formattedPhoneNumber.prefix || '',
    });
    formApi.setValue('driverId', customer.id);
    formApi.setValue('driverContactId', customer.primaryContactId ?? null);
    formApi.setValue('contactPerson', customer.id);
    formApi.setValue('driverIdentityCard', customerInfo.identityCards?.[0]?.cardNumber ?? '');
    refreshPhoneInputKey();
  });

  const handleEditCustomer = () => {
    editCustomer?.();
  };

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.person.labels.contactInformation')}
      hasSeparator
    >
      <VStack spacing={4}>
        <FormField name="driverId" type="hidden" control={props.control} />
        <FormField name="driverContactId" type="hidden" control={props.control} />
        <Hide when={props.isReadOnly}>
          <CustomerSelectSearch
            onCustomerSelect={handleCustomerSelect}
            onAfterCustomerSelect={refreshPhoneInputKey}
          />
        </Hide>

        <Stack
          width="100%"
          direction={['column', 'column', 'column', 'row']}
          spacing={4}
          align="flex-end"
        >
          <Grid columns={[2, 2, 2, 4]} grow={1} spacing={4} align="flex-end">
            <GridItem>
              <FormField
                label={i18n.t('general.labels.nameAndSurname')}
                name="driverFullName"
                type="text"
                isDisabled
                control={props.control}
                data-testid={suffixTestId('driver', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                key={props.phoneInputKey}
                label={i18n.t('entity.phoneNumber.labels.number')}
                name="driverPhone"
                type="phone"
                isDisabled
                control={props.control}
                countries={countriesOptions}
                data-testid={suffixTestId('driver', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                label={i18n.t('general.labels.emailAddress')}
                name="driverEmail"
                type="email"
                isDisabled
                control={props.control}
                data-testid={suffixTestId('email', props)}
              />
            </GridItem>
            <GridItem>
              <FormField
                label={i18n.t('general.labels.identityCard')}
                name="driverIdentityCard"
                type="text"
                isDisabled
                control={props.control}
                data-testid={suffixTestId('identityCard', props)}
              />
            </GridItem>
          </Grid>

          <HStack spacing={1}>
            <Tooltip label={i18n.t('entity.customer.actions.editCustomer')}>
              <IconButton
                onClick={handleEditCustomer}
                priority="secondary"
                icon="image/edit"
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('editCustomer', props)}
                aria-label={i18n.t('entity.customer.actions.editCustomer')}
              />
            </Tooltip>
            <Tooltip label={i18n.t('entity.customer.actions.createNewCustomer')}>
              <IconButton
                onClick={handleCreateCustomer}
                priority="secondary"
                icon="content/add_circle"
                isDisabled={props.isReadOnly}
                data-testid={suffixTestId('createCustomer', props)}
                aria-label={i18n.t('entity.customer.actions.createNewCustomer')}
              />
            </Tooltip>
          </HStack>
        </Stack>
      </VStack>
    </Card>
  );
};
